// Use new Date(y, m, d) for local date math/parsing to avoid DST and UTC timezone date-shift issues

/** Day index a week starts on: 0 = Sunday ... 6 = Saturday. */
export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** A start/end pair. `to` is absent while a range pick is still in progress. */
export interface DateRangeValue {
  from: Date;
  to?: Date;
}

/** Describes a set of days: `disabledDates`, and internally min/max bounds. */
export type DateMatcher =
  | Date
  | Date[]
  | { from: Date; to?: Date }
  | { before: Date }
  | { after: Date }
  | { dayOfWeek: number[] }
  | ((date: Date) => boolean);

/* Primitives */

export function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

export function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

/** Midnight on the same calendar day, in local time. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Day 0 of the *next* month is the last day of this one. */
export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function daysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

/** Today at midnight. */
export function today(): Date {
  return startOfDay(new Date());
}

/** Add days, preserving wall-clock time across DST transitions. */
export function addDays(date: Date, amount: number): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + amount,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );
}

/** Add months, clamping the day so `Jan 31 + 1 month` is Feb 28/29. */
export function addMonths(date: Date, amount: number): Date {
  const year = date.getFullYear();
  const targetMonth = date.getMonth() + amount;
  const lastDay = new Date(year, targetMonth + 1, 0).getDate();
  return new Date(
    year,
    targetMonth,
    Math.min(date.getDate(), lastDay),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );
}

/** Delegates to `addMonths` so Feb 29 clamps to Feb 28 in a non-leap year. */
export function addYears(date: Date, amount: number): Date {
  return addMonths(date, amount * 12);
}

/** Midnight on the first day of the week containing `date`. */
export function startOfWeek(date: Date, weekStartsOn: WeekStart = 0): Date {
  const offset = (date.getDay() - weekStartsOn + 7) % 7;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - offset);
}

export function endOfWeek(date: Date, weekStartsOn: WeekStart = 0): Date {
  return addDays(startOfWeek(date, weekStartsOn), 6);
}

/* Comparison -- all at day granularity unless named otherwise */

/** -1 if `a` is an earlier day than `b`, 1 if later, 0 if the same day. */
export function compareDay(a: Date, b: Date): number {
  const ay = a.getFullYear();
  const by = b.getFullYear();
  if (ay !== by) return ay < by ? -1 : 1;
  const am = a.getMonth();
  const bm = b.getMonth();
  if (am !== bm) return am < bm ? -1 : 1;
  const ad = a.getDate();
  const bd = b.getDate();
  if (ad !== bd) return ad < bd ? -1 : 1;
  return 0;
}

export function isSameDay(a?: Date | null, b?: Date | null): boolean {
  if (!isValidDate(a) || !isValidDate(b)) return false;
  return compareDay(a, b) === 0;
}

export function isSameMonth(a?: Date | null, b?: Date | null): boolean {
  if (!isValidDate(a) || !isValidDate(b)) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

export function isBeforeDay(a: Date, b: Date): boolean {
  return compareDay(a, b) < 0;
}

export function isAfterDay(a: Date, b: Date): boolean {
  return compareDay(a, b) > 0;
}

/** Inclusive on both ends; an open-ended range (`to` absent) matches only `from`. */
export function isDayInRange(date: Date, range?: DateRangeValue): boolean {
  if (!range?.from || !isValidDate(range.from)) return false;
  if (!range.to || !isValidDate(range.to)) return isSameDay(date, range.from);
  return compareDay(date, range.from) >= 0 && compareDay(date, range.to) <= 0;
}

/** Order two endpoints so `from <= to` regardless of which was picked first. */
export function orderRange(a: Date, b: Date): DateRangeValue {
  return compareDay(b, a) < 0 ? { from: b, to: a } : { from: a, to: b };
}

/** Pull `date` inside `[min, max]`. Either bound may be omitted. */
export function clampDate(date: Date, min?: Date, max?: Date): Date {
  if (min && compareDay(date, min) < 0) return min;
  if (max && compareDay(date, max) > 0) return max;
  return date;
}

/* Serialization */

/** Local calendar day as `"YYYY-MM-DD"`. Never touches UTC. */
export function ymd(date: Date): string {
  return `${String(date.getFullYear()).padStart(4, "0")}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Parse `"YYYY-MM-DD"` to local midnight; `undefined` if malformed or overflowing. */
export function parseYmd(input: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(input);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;

  const date = new Date(year, month - 1, day);
  // Years 0-99 are remapped to 1900-1999 by the constructor; undo that.
  if (year < 100) date.setFullYear(year);
  // Reject overflow: Feb 31 would have rolled into March.
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return undefined;
  return date;
}

/* Time of day */

export function formatHm(hours: number, minutes: number): string {
  return `${pad2(hours)}:${pad2(minutes)}`;
}

/** Parse 24-hour `"HH:MM"`. Returns `undefined` if out of range or malformed. */
export function parseHm(input: string): { hours: number; minutes: number } | undefined {
  const match = /^(\d{1,2}):(\d{2})$/.exec(input?.trim() ?? "");
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return undefined;
  return { hours, minutes };
}

/** Copy `date` with the time of day replaced. */
export function setTime(date: Date, hours: number, minutes: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0);
}

/* Month grid */

interface MonthGridOptions {
  weekStartsOn?: WeekStart;
  /** Always emit 6 weeks, so the popover height does not jump between months. */
  fixedWeeks?: boolean;
}

/** Every day cell for `month`, using real neighbouring days, not `null` padding. */
export function buildMonthGrid(month: Date, options: MonthGridOptions = {}): Date[] {
  const { weekStartsOn = 0, fixedWeeks = true } = options;
  const first = startOfMonth(month);
  const gridStart = startOfWeek(first, weekStartsOn);

  let weeks = 6;
  if (!fixedWeeks) {
    const leading = (first.getDay() - weekStartsOn + 7) % 7;
    weeks = Math.ceil((leading + daysInMonth(month)) / 7);
  }

  const cells: Date[] = [];
  for (let i = 0; i < weeks * 7; i += 1) {
    cells.push(addDays(gridStart, i));
  }
  return cells;
}

/** Split a flat grid into rows of 7 for `role="row"` rendering. */
export function chunkWeeks(cells: Date[]): Date[][] {
  const weeks: Date[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

/* Matchers */

function matchesOne(date: Date, matcher: DateMatcher): boolean {
  if (typeof matcher === "function") return matcher(date);
  if (matcher instanceof Date) return isSameDay(date, matcher);
  if (Array.isArray(matcher)) return matcher.some((d) => isSameDay(date, d));
  if ("before" in matcher) return isBeforeDay(date, matcher.before);
  if ("after" in matcher) return isAfterDay(date, matcher.after);
  if ("dayOfWeek" in matcher) return matcher.dayOfWeek.includes(date.getDay());
  if ("from" in matcher) return isDayInRange(date, matcher);
  return false;
}

/** True if `date` satisfies any of the given matchers. */
export function isMatch(date: Date, matcher?: DateMatcher | DateMatcher[]): boolean {
  if (!matcher) return false;
  if (Array.isArray(matcher) && matcher.every((m) => m instanceof Date)) {
    // A bare Date[] is one matcher ("any of these days"), not a matcher list.
    return matchesOne(date, matcher as Date[]);
  }
  if (Array.isArray(matcher)) {
    return (matcher as DateMatcher[]).some((m) => matchesOne(date, m));
  }
  return matchesOne(date, matcher);
}

/** Fold `disabledDates`, `minDate` and `maxDate` into one matcher list. */
export function composeDisabled(
  disabledDates?: DateMatcher | DateMatcher[],
  minDate?: Date,
  maxDate?: Date,
): DateMatcher[] {
  const parts: DateMatcher[] = [];
  if (disabledDates) {
    if (Array.isArray(disabledDates) && disabledDates.every((m) => m instanceof Date)) {
      parts.push(disabledDates as Date[]);
    } else if (Array.isArray(disabledDates)) {
      parts.push(...(disabledDates as DateMatcher[]));
    } else {
      parts.push(disabledDates);
    }
  }
  if (minDate) parts.push({ before: minDate });
  if (maxDate) parts.push({ after: maxDate });
  return parts;
}
